import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useCompetencia } from '../../context/CompetenciaContext';
 
// Tus pasos o subcomponentes
import StepAtleta from '../../components/gestionEvento/RegistroFrom/StepAtleta';
import StepRelevos from '../../components/gestionEvento/RegistroFrom/StepRelevos';
import StepPago from '../../components/gestionEvento/RegistroFrom/StepPago';

// Nuevo componente para mostrar estado de inscripciones
import RegistrationStatusMessage from './RegistrationStatusMessage';

// Utilidades
import { computeAge } from '../../utils/dateUtils';
import { createOrUpdateTeam } from '../../utils/teamUtils';
import optimizeAndConvertToWebP from '../../utils/optimizeAndConvertToWebP';
import { generateCodigo } from '../../utils/generateCodigo';
import { getRegistrationStatus } from '../../utils/registrationStatusUtils';

// Definición de validaciones (sin cambios)
const currentYear = new Date().getFullYear();
const numericTransform = (value, originalValue) => {
  if (originalValue === '' || originalValue === null || typeof originalValue === 'undefined') {
    return undefined; // permite que required dispare en vacío en vez de min/max
  }
  const n = Number(originalValue);
  return Number.isNaN(n) ? undefined : n;
};

const validationSchema = Yup.object({
  email: Yup.string().email('Email no válido').required('Campo obligatorio'),
  fotoPerfil: Yup.string(),
  nombre: Yup.string().required('Campo obligatorio'),
  apellido: Yup.string().required('Campo obligatorio'),
  rama: Yup.string().required('Campo obligatorio'),
  diaNacimiento: Yup.number()
    .transform(numericTransform)
    .min(1, 'El día mínimo es 1')
    .max(31, 'El día máximo es 31')
    .required('Campo obligatorio'),
  mesNacimiento: Yup.number()
    .transform(numericTransform)
    .min(1, 'El mes mínimo es 1')
    .max(12, 'El mes máximo es 12')
    .required('Campo obligatorio'),
  anioNacimiento: Yup.number()
    .transform(numericTransform)
    .typeError('Año inválido')
    .integer('Año inválido')
    .min(1900, 'Año mínimo 1900')
    .max(currentYear, `Año máximo ${currentYear}`)
    .required('Campo obligatorio'),
  equipo: Yup.mixed().required('Campo obligatorio'),
  logoEquipo: Yup.string(),
  categoriaId: Yup.string().required('Debes seleccionar una categoría'),
  categoria: Yup.string(),
  catNumber: Yup.string(),
  direccion: Yup.string(),
  ciudad: Yup.string(),
  estado: Yup.string(),
  pais: Yup.string(),
  telefono: Yup.string(),
  asociacion: Yup.string(),

  // Ajustado: relevosData es un array de objetos con asignacion, posicion, equipoTipo, etc.
  relevosData: Yup.array().of(
    Yup.object({
      prueba: Yup.string(),       // si se usa el campo 'prueba'
      tipoMarca: Yup.string(),    // si usas la marca en relevos
      asignacion: Yup.string(),   // A, B, C, ...
      posicion: Yup.string(),     // "Integrante 1", "Suplente 2", etc.
      equipoTipo: Yup.string(),   // "Varonil", "Femenil", "Mixto"
    })
  ),

  pruebasData: Yup.array().of(
    Yup.object({
      prueba: Yup.string().required('Campo obligatorio'),
      tipoMarca: Yup.string(),
    })
  ),
  contactoEmergencia: Yup.array().of(
    Yup.object({
      nombreEmergencia: Yup.string().required('Campo obligatorio'),
      telefonoEmergencia: Yup.string().required('Campo obligatorio'),
    })
  ),
  menorEdad: Yup.string(),
  tutorNombre: Yup.string(),
  tutorEmail: Yup.string().email('Email no válido'),
  tutorTelefono: Yup.string(),
  codigoId: Yup.string(),
});

const RegistroForm = () => {
  const { slug, competitionId } = useParams();
  const { competencias, addInscripcion, getCategorias, fetchPruebas } = useCompetencia();

  // ** TODOS LOS HOOKS DEBEN IR AL INICIO ANTES DE CUALQUIER RETURN **
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [pruebasFiltradas, setPruebasFiltradas] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // Derivar datos de competencia: por id o por slug
  const competencia = React.useMemo(() => {
    return (
      competencias.find((comp) => comp.id === competitionId) ||
      competencias.find((comp) => comp.slug === slug)
    );
  }, [competencias, competitionId, slug]);
  const config = competencia ? competencia.registration?.config || {} : {};
  const registroFields = config.registroFields || {};

  // Categorías disponibles: usar las del contexto si existen; si no, cargar perezosamente
  const [categoriasDisponibles, setCategoriasDisponibles] = useState(
    competencia?.categorias && competencia.categorias.length > 0 ? competencia.categorias : []
  );

  // Lista completa de pruebas (cargada perezosamente si hace falta)
  const [pruebasAll, setPruebasAll] = useState(competencia?.pruebas || []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!competencia?.id) return;
      if (categoriasDisponibles.length > 0) return;
      try {
        const cats = await getCategorias(competencia.id);
        if (mounted && Array.isArray(cats)) setCategoriasDisponibles(cats);
      } catch (e) {
        // Silenciar; el formulario sigue funcionando sin autoselección
        console.warn('No se pudieron cargar categorías:', e);
      }
    })();
    return () => { mounted = false; };
  }, [competencia?.id, getCategorias, categoriasDisponibles.length]);

  // ** NUEVA LÓGICA: Verificar estado de inscripciones **
  const registrationStatus = competencia ? getRegistrationStatus(
    competencia.general?.fechaInicioInscripciones,
    competencia.general?.fechaFinInscripciones
  ) : { canRegister: false, status: 'unknown' };

  // Si la competencia requiere relevos, 3 pasos; si no, 2 pasos
  const steps = competencia?.rules?.requerirMiembrosRelevo
    ? ['Datos del Atleta', 'Datos de Relevos', 'Información de Pago']
    : ['Datos del Atleta', 'Información de Pago'];

  // Inicializamos arrays
  const initialContacto = registroFields.contactoEmergencia
    ? [{ nombreEmergencia: '', telefonoEmergencia: '' }]
    : [];
  const initialPruebas = [{ prueba: '', tipoMarca: '' }];

  // Ajustado: relevosData inicial con un solo relevo (o varios, si lo deseas)
  const initialRelevos = competencia && competencia.rules?.opcionesRelevos
    ? [
        {
          prueba: '',
          tipoMarca: '',
          asignacion: '',
          posicion: '',
          equipoTipo: '', // Se setea luego
        },
      ]
    : [];

  // ** TODOS LOS HOOKS DEBEN ESTAR AQUÍ ANTES DE CUALQUIER RETURN **
  // useFormik
const formik = useFormik({
  initialValues: {
    email: '',
    fotoPerfil: '',
    nombre: '',
    apellido: '',
    rama: '',
    diaNacimiento: '',
    mesNacimiento: '',
    anioNacimiento: '',
    equipo: null,
    // Los siguientes se usan en el UI, pero NO se guardarán en Firestore:
    logoEquipo: '',
    categoriaId: '',
    catNumber: '',
    categoria: '',
    // Campos opcionales de contacto/ubicación (UI):
    direccion: '',
    ciudad: '',
    estado: '',
    pais: '',
    telefono: '',
    asociacion: '',
    // Arrays del formulario:
    pruebasData: initialPruebas,
    relevosData: initialRelevos,
    contactoEmergencia: initialContacto,
    // Tutoría (si menor de edad):
    menorEdad: '',
    tutorNombre: '',
    tutorEmail: '',
    tutorTelefono: '',
    // Código visible en el recibo:
    codigoId: '',
    // Solo para referencia en el UI; no se confía en este valor para escribir:
    competenciaId: competencia ? competencia.id : '',
  },

  validationSchema,

  onSubmit: async (values) => {
    // Helper para componer fecha de nacimiento (YYYY-MM-DD)
    const pad = (n) => String(n).padStart(2, '0');
    const fechaNacimiento = `${values.anioNacimiento}-${pad(values.mesNacimiento)}-${pad(values.diaNacimiento)}`;

    // ---------- 1) Resolver categoría seleccionada ----------
    const sourceCats =
      (competencia?.categorias?.length ? competencia.categorias : categoriasDisponibles) || [];
    const selectedCat = sourceCats.find((c) => c.id === values.categoriaId);

    if (!selectedCat) {
      setSnackbarMessage('Categoría no encontrada. Revisa tu selección.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Objeto de categoría reducido (sin catNumber)
    const categoriaObj = { id: selectedCat.id, nombre: selectedCat.nombre };

    // ---------- 2) Resolver pruebas / relevos con forma mínima ----------
    const sourceTests = (competencia?.pruebas?.length ? competencia.pruebas : pruebasAll) || [];
    const findTest = (nombre) =>
      sourceTests.find((t) => (t?.nombre || '').toLowerCase() === (nombre || '').toLowerCase());

    // Conserva solo campos de marca válidos si existen
    const mapMarcaCampos = (p) => {
      const keep = {};
      [
        'marcaHoras',
        'marcaMinutos',
        'marcaSegundos',
        'marcaCentesimas',
        'marcaMetros',
        'marcaCentimetros',
        'marcaPuntos',
      ].forEach((k) => {
        if (p?.[k] !== undefined && p?.[k] !== null && p?.[k] !== '') keep[k] = p[k];
      });
      return keep;
    };

    // Pruebas individuales: SIN catNumber / SIN eventcode (mal escrito)
    const pruebas = (values.pruebasData || [])
      .filter((p) => p?.prueba)
      .map((p) => {
        const mt = findTest(p.prueba);
        return {
          prueba: p.prueba,
          tipoMarca: p.tipoMarca || '',
          eventCode: mt?.eventCode || p.eventCode || '',
          categoriaId: categoriaObj.id, // referencia a la categoría correcta
          ...mapMarcaCampos(p),
        };
      });

    // Relevos: SIN catNumber / SIN categoriaId / SIN integrantes/suplentes
    const relevosData = (values.relevosData || [])
      .filter((r) => r?.prueba)
      .map((r) => {
        const mt = findTest(r.prueba);
        return {
          prueba: r.prueba,
          tipoMarca: r.tipoMarca || '',
          eventCode: mt?.eventCode || r.eventCode || '',
          equipoTipo: r.equipoTipo || '',
          asignacion: r.asignacion || '',
          posicion: r.posicion || '',
          ...mapMarcaCampos(r),
        };
      });

    // ---------- 3) Normalizar equipo ----------
    let equipoNorm = values.equipo;
    if (!equipoNorm || typeof equipoNorm !== 'object' || !equipoNorm.id || !equipoNorm.teamCode) {
      const { team } = createOrUpdateTeam(
        competencia,
        typeof values.equipo === 'string' ? values.equipo : values.equipo?.name,
        values.equipo?.logoEquipo
      );
      equipoNorm = team;
    }

    // ---------- 4) Generar código si falta ----------
    const codigoId = values.codigoId || generateCodigo(values.nombre, values.apellido);

    // ---------- 5) Validación de competencia ----------
    if (!competencia) {
      setSnackbarMessage('Error: Competencia no encontrada. Revisa la URL o inténtalo más tarde.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // ---------- 6) Armar payload FINAL que se guardará ----------
    // OJO: solo incluimos campos permitidos
    const payload = {
      competenciaId: competencia.id,
      codigoId,
      email: values.email,
      fotoPerfil: values.fotoPerfil || '',
      nombre: values.nombre,
      apellido: values.apellido,
      rama: values.rama,
      diaNacimiento: values.diaNacimiento,
      mesNacimiento: values.mesNacimiento,
      anioNacimiento: values.anioNacimiento,
      fechaNacimiento,
      telefono: values.telefono || '',
      asociacion: values.asociacion || '',
      direccion: values.direccion || '',
      ciudad: values.ciudad || '',
      estado: values.estado || '',
      pais: values.pais || '',
      contactoEmergencia: Array.isArray(values.contactoEmergencia) ? values.contactoEmergencia : [],
      menorEdad: values.menorEdad || '',
      tutorNombre: values.tutorNombre || '',
      tutorEmail: values.tutorEmail || '',
      tutorTelefono: values.tutorTelefono || '',
      equipo: {
        id: equipoNorm.id,
        name: equipoNorm.name,
        teamCode: equipoNorm.teamCode,
        logoEquipo: equipoNorm.logoEquipo || '',
      },
      categoria: categoriaObj, // <- { id, nombre }
      pruebas,                 // <- array saneado
      relevosData,             // <- array saneado
      // NO ponemos fechaRegistro (la setea addInscripcion con Timestamp.now())
      // NO ponemos estadoPago aquí (addInscripcion = 'pendiente')
      // NO ponemos catNumber/categoriaId top-level
      // NO guardamos pruebasData (solo pruebas)
    };

    // ---------- 7) Guardar en Firestore (subcolección inscripciones) ----------
    console.log('[RegistroForm] Guardando inscripción con payload:', payload);
    try {
      const resultado = await addInscripcion(competencia.id, payload);
      console.log('[RegistroForm] Resultado addInscripcion:', resultado);

      if (resultado?.wasAdded) {
        setSnackbarMessage(`Inscripción exitosa con el código: ${codigoId}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setReceiptData({ ...payload, id: resultado.id });
        setSubmitSuccess(true);
      } else {
        const msg = resultado?.reason || 'No se pudo guardar la inscripción.';
        setSnackbarMessage(`Error al guardar la inscripción: ${msg}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        console.error('Error al registrar:', resultado);
      }
    } catch (e) {
      console.error('[RegistroForm] Excepción al guardar inscripción:', e);
      setSnackbarMessage(`Error inesperado al guardar: ${e?.message || e}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  },
});


  // Efecto para actualizar el código cada vez que cambian nombre o apellido
  useEffect(() => {
    const { nombre, apellido } = formik.values;
    if (nombre.trim() !== '' || apellido.trim() !== '') {
      const nuevoCodigo = generateCodigo(nombre, apellido);
      formik.setFieldValue('codigoId', nuevoCodigo);
    }
  }, [formik.values.nombre, formik.values.apellido]);

  // Efecto para autoseleccionar la categoría según la edad
  useEffect(() => {
    if (!competencia) return;
    if (!Array.isArray(categoriasDisponibles) || categoriasDisponibles.length === 0) return;
    const age = computeAge(
      formik.values.anioNacimiento,
      formik.values.mesNacimiento,
      formik.values.diaNacimiento
    );
    if (!age) return;

    const matches = categoriasDisponibles.filter((cat) => {
      if (typeof cat.edadMin === 'number' && typeof cat.edadMax === 'number') {
        return age >= cat.edadMin && age <= cat.edadMax;
      }
      return false;
    });
    if (matches.length >= 1 && (!formik.values.categoriaId || !formik.values.categoria)) {
      const pick = [...matches].sort((a, b) => {
        const aHas = typeof a.catNumber === 'number';
        const bHas = typeof b.catNumber === 'number';
        if (aHas && bHas) return a.catNumber - b.catNumber;
        if (aHas) return -1;
        if (bHas) return 1;
        const aSpan = (a.edadMax ?? 999) - (a.edadMin ?? 0);
        const bSpan = (b.edadMax ?? 999) - (b.edadMin ?? 0);
        if (aSpan !== bSpan) return aSpan - bSpan;
        return (a.edadMin ?? 0) - (b.edadMin ?? 0);
      })[0];
      formik.setFieldValue('categoriaId', pick.id);
      formik.setFieldValue('categoria', pick.nombre);
      formik.setFieldValue('catNumber', pick.catNumber);
    }
  }, [
    formik.values.anioNacimiento,
    formik.values.mesNacimiento,
    formik.values.diaNacimiento,
    categoriasDisponibles,
    competencia,
    formik,
  ]);

  // Filtrar pruebas según categoría/rama, etc.
  useEffect(() => {
    if (!competencia) return;
    const { categoriaId, rama } = formik.values;
    if (categoriaId && rama) {
      const cat = categoriasDisponibles?.find((c) => c.id === categoriaId);
      if (cat) {
        const source = pruebasAll; // usar lista cargada
        const filtered = source.filter((prueba) => {
          if (rama.toLowerCase() === 'femenil') {
            return (
              Array.isArray(prueba.categoriaIdsFemenil) &&
              prueba.categoriaIdsFemenil.includes(cat.id)
            );
          } else if (rama.toLowerCase() === 'varonil') {
            return (
              Array.isArray(prueba.categoriaIdsVaronil) &&
              prueba.categoriaIdsVaronil.includes(cat.id)
            );
          }
          return true;
        });
        setPruebasFiltradas(filtered);
      } else {
        setPruebasFiltradas(pruebasAll);
      }
    } else {
      setPruebasFiltradas(pruebasAll);
    }
  }, [formik.values.categoriaId, formik.values.rama, competencia, categoriasDisponibles, /* fuente */ pruebasAll]);

  // Carga perezosa de pruebas si no están en memoria
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!competencia?.id) return;
      if (Array.isArray(competencia.pruebas) && competencia.pruebas.length > 0) {
        setPruebasAll(competencia.pruebas);
        return;
      }
      try {
        const tests = await fetchPruebas(competencia.id);
        if (mounted && Array.isArray(tests)) setPruebasAll(tests);
      } catch (e) {
        console.warn('No se pudieron cargar pruebas:', e);
      }
    })();
    return () => { mounted = false; };
  }, [competencia?.id, competencia?.pruebas, fetchPruebas]);

  // ** FUNCIONES DE MANEJO **
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Función para volver al evento (opcional)
  const handleBackToEvent = () => {
    // Podrías usar router para navegar, o simplemente hacer scroll arriba
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // O si tienes router: navigate(`/evento/${slug}`);
  };

  const handleFileChange = async (e, fieldName) => {
    const { files } = e.target;
    if (files && files[0]) {
      const optimizedImage = await optimizeAndConvertToWebP(files[0]);
      if (optimizedImage) {
        formik.setFieldValue(fieldName, optimizedImage);
      }
    }
  };

const handleNext = async () => {
  // 0) Normalizar/limpiar filas vacías antes de validar
  const cleanedPruebas = (formik.values.pruebasData || []).filter(
    (p) => (p?.prueba || '').trim() !== ''
  );
  if (cleanedPruebas.length !== (formik.values.pruebasData || []).length) {
    // NO disparar validación aquí
    formik.setFieldValue('pruebasData', cleanedPruebas, false);
  }

  const cleanedRelays = (formik.values.relevosData || []).filter((r) => {
    const hasPrueba = (r?.prueba || '').trim() !== '';
    const hasAlguno = !!(r?.posicion || r?.asignacion || r?.equipoTipo);
    return hasPrueba || hasAlguno;
  });
  if (cleanedRelays.length !== (formik.values.relevosData || []).length) {
    formik.setFieldValue('relevosData', cleanedRelays, false);
  }

  // 1) Validar con Yup ya con arrays limpios
  const errors = await formik.validateForm();

  const camposPaso0 = [
    'email',
    'nombre',
    'apellido',
    'rama',
    'diaNacimiento',
    'mesNacimiento',
    'anioNacimiento',
    'equipo',
    'categoriaId',
  ];

  let hayError = false;

  // Errores de campos básicos
  for (const campo of camposPaso0) {
    if (errors[campo]) {
      hayError = true;
    }
  }

  // 2) Requerir "al menos 1" prueba (no todas)
  const hasOnePrueba = cleanedPruebas.length > 0;
  if (!hasOnePrueba) {
    hayError = true;
    // Aviso visible para el usuario
    setSnackbarMessage('Debes seleccionar al menos una prueba.');
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  }

  // 3) Si tienes contactoEmergencia obligatorio en tu config, validarlo aquí
  //    (respetando tu lógica actual)
  if (registroFields.contactoEmergencia) {
    const ce = formik.values.contactoEmergencia || [];
    if (ce.length < 1 || ce.some((c) => !c?.nombreEmergencia || !c?.telefonoEmergencia)) {
      hayError = true;
    }
  }

  if (hayError) {
    const touched = Object.fromEntries(camposPaso0.map((c) => [c, true]));
    // Marca el array como tocado para que el UI muestre feedback
    touched['pruebasData'] = true;
    if (registroFields.contactoEmergencia) touched['contactoEmergencia'] = true;
    formik.setTouched(touched, true);
    return;
  }

  // 4) Avance de step
  // Si tu competencia maneja Relevos y escribiste datos de relevos, ve al step de Relevos;
  // si no, salta directo a Pago.
  if (activeStep === 0 && competencia?.rules?.requerirMiembrosRelevo) {
    const hasRelayData = cleanedRelays.some((relay) => (relay?.prueba || '').trim() !== '');
    setActiveStep(hasRelayData ? 1 : 2);
    return;
  }

  setActiveStep((prev) => prev + 1);
};


  const handleBack = () => {
    // Si está en el paso 2 (pago) y la competencia requiere relevos,
    // regresar al step 1 si hay datos de relevos
    if (activeStep === 2 && competencia?.rules?.requerirMiembrosRelevo) {
      const hasRelayData = formik.values.relevosData.some(
        (relay) => relay.prueba && relay.prueba.trim() !== ''
      );
      setActiveStep(hasRelayData ? 1 : 0);
      return;
    }
    // Si está en step 1 con relevos, volver a step 0
    if (activeStep === 1 && competencia?.rules?.requerirMiembrosRelevo) {
      setActiveStep(0);
      return;
    }
    // Sino, decrementa normalmente
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const handleResetForm = () => {
    formik.resetForm({
      values: {
        ...formik.initialValues,
        codigoId: '', // Dejar vacío para que se regenere
      },
    });
    setActiveStep(0);
  };

  // Determina qué componente se renderiza en cada step
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepAtleta
            formik={formik}
            handleBack={handleBack}
            handleNext={handleNext}
            equiposDisponibles={competencia?.equipos || []}
            categoriasDisponibles={categoriasDisponibles}
            pruebasFiltradas={pruebasFiltradas}
            maxPruebas={competencia?.rules?.maxPruebasIndPorRegistro || Infinity}
            registroFields={registroFields}
            registroFieldLabels={config.registroFieldLabels || {}}
            habilitarMarcasSiembra={competencia?.rules?.habilitarMarcasSiembra}
            handleFileChange={handleFileChange}
            maxRelevos={competencia?.rules?.maxRelevosPorRegistro || Infinity}
            competencia={competencia}
          />
        );
      case 1:
        // Renderiza StepRelevos solo si se requieren
        return competencia?.rules?.requerirMiembrosRelevo ? (
          <StepRelevos
            formik={formik}
            handleBack={handleBack}
            handleNext={handleNext}
            opcionesRelevos={competencia?.rules?.opcionesRelevos}
            equiposDisponibles={competencia?.equipos || []}
          />
        ) : (
          <StepPago
            handleBack={handleBack}
            handleReset={handleResetForm}
            formData={formik.values}
            onEnviarInscripcion={formik.handleSubmit}
            success={submitSuccess}
            receiptData={receiptData}
          />
        );
case 2:
  return (
    <StepPago
      handleBack={handleBack}
      handleReset={handleResetForm}
      formData={formik.values}
      onEnviarInscripcion={formik.handleSubmit}
      success={submitSuccess}
      receiptData={receiptData}
    />
  );
      default:
        return null;
    }
  };

  // ** RENDERIZADO TEMPRANO: Después de todos los hooks **
  if (!competencia) {
    return (
      <Box>
        <Typography variant="h6" color="error" align="center">
          Competencia no encontrada.
        </Typography>
      </Box>
    );
  }

  // ** VERIFICACIÓN PRINCIPAL: Si las inscripciones no están abiertas **
  if (!registrationStatus.canRegister) {
    return (
      <RegistrationStatusMessage
        status={registrationStatus.status}
        message={registrationStatus.message}
        startDate={registrationStatus.startDate}
        endDate={registrationStatus.endDate}
        competenciaName={competencia.general?.nombre}
        onBackToEvent={handleBackToEvent}
      />
    );
  }

  // ** RENDERIZADO NORMAL DEL FORMULARIO **

  return (
    <FormikProvider value={formik}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Regístrate a {competencia.general?.nombre}
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <form onSubmit={formik.handleSubmit}>
          {renderStepContent(activeStep)}
        </form>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </FormikProvider>
  );
};

export default RegistroForm;
